import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/api';

interface AdverseEventWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AdverseEventWizard: React.FC<AdverseEventWizardProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({
    severity: 'mild',
    report_source: 'consumer',
    status: 'pending',
    priority: 'normal',
    death: false,
    life_threatening: false,
    hospitalization: false,
    significant_disability: false,
    congenital_anomaly: false,
    required_intervention: false,
    significant_disfigurement: false,
    infection: false,
    is_adverse_event: false,
    is_serious_adverse_event: false,
    is_serious: false
  });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Array<{ id: number; name: string }>>([]);

  const steps = [
    'Basic Information',
    'Patient Details', 
    'Event Details',
    'SAE Criteria',
    'Medical Information',
    'Reporter Information',
    'Product Information',
    'Review & Submit'
  ];

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/products/`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
        });
        if (res.ok) {
          const data = await res.json();
          setProducts(data);
        }
      } catch (error) {
        console.error('Error loading products:', error);
      }
    };
    loadProducts();
  }, []);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/adverse-events/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        onSuccess();
        onClose();
        setFormData({});
        setCurrentStep(0);
      } else {
        console.error('Failed to create adverse event');
      }
    } catch (error) {
      console.error('Error creating adverse event:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Information
  return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product *
              </label>
              <select
                value={formData.product_id || ''}
                onChange={(e) => handleInputChange('product_id', parseInt(e.target.value))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              >
                <option value="">Select product</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
        </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                Description *
            </label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleInputChange('description', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={4}
                placeholder="Describe the adverse event in detail..."
                required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Severity
            </label>
            <select
                value={formData.severity || 'mild'}
              onChange={(e) => handleInputChange('severity', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="mild">Mild</option>
              <option value="moderate">Moderate</option>
              <option value="severe">Severe</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                Received Date
              </label>
              <input
                type="date"
                value={formData.received_date || ''}
                onChange={(e) => handleInputChange('received_date', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        );

      case 1: // Patient Details
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient Age
                </label>
                <input
                  type="number"
                  value={formData.patient_age || ''}
                  onChange={(e) => handleInputChange('patient_age', parseInt(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Age in years"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient Gender
                </label>
                <select
                  value={formData.patient_gender || ''}
                  onChange={(e) => handleInputChange('patient_gender', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient Weight (kg)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.patient_weight || ''}
                  onChange={(e) => handleInputChange('patient_weight', parseFloat(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Weight in kg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient Height (cm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.patient_height || ''}
                  onChange={(e) => handleInputChange('patient_height', parseFloat(e.target.value))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Height in cm"
                />
              </div>
            </div>
          </div>
        );

      case 2: // Event Details
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Date
              </label>
              <input
                type="date"
                value={formData.event_date || ''}
                onChange={(e) => handleInputChange('event_date', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Location (Body Part)
              </label>
              <input
                type="text"
                value={formData.event_location || ''}
                onChange={(e) => handleInputChange('event_location', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., face, arm, leg, torso"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Event Outcome
            </label>
            <select
                value={formData.event_outcome || ''}
                onChange={(e) => handleInputChange('event_outcome', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Select outcome</option>
                <option value="recovered">Recovered</option>
                <option value="recovering">Recovering</option>
                <option value="not_recovered">Not Recovered</option>
                <option value="fatal">Fatal</option>
                <option value="unknown">Unknown</option>
            </select>
            </div>
          </div>
        );

      case 3: // SAE Criteria
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Serious Adverse Event (SAE) Criteria - MoCRA Compliant</h3>
              <div className="text-blue-800 space-y-2 text-sm">
                <p><strong>SAE Definition:</strong> An adverse event that results in ANY of the specific outcomes below.</p>
                <div className="bg-yellow-100 border border-yellow-300 rounded p-2 mt-2">
                  <p className="text-yellow-800 font-semibold">📋 Important:</p>
                  <p className="text-yellow-700">• <strong>Severity alone does NOT determine SAE status</strong> - only the specific criteria below</p>
                  <p className="text-yellow-700">• A "severe" event is NOT automatically an SAE unless it meets one of the criteria below</p>
                  <p className="text-yellow-700">• SAE status is determined by checking the boxes below, not by severity level</p>
                </div>
                <p className="font-semibold text-red-700">⚠️ SAEs must be reported to FDA within 15 business days!</p>
              </div>
            </div>
            
            <div className="text-sm text-gray-600 mb-4">
              Check all Serious Adverse Event (SAE) criteria that apply:
            </div>
            
            <div className="space-y-3">
              {[
                { key: 'death', label: 'Death' },
                { key: 'life_threatening', label: 'Life-threatening' },
                { key: 'hospitalization', label: 'Hospitalization or prolongation of hospitalization' },
                { key: 'significant_disability', label: 'Significant disability/incapacity' },
                { key: 'congenital_anomaly', label: 'Congenital anomaly/birth defect' },
                { key: 'required_intervention', label: 'Required intervention to prevent permanent impairment' },
                { key: 'significant_disfigurement', label: 'Significant disfigurement' },
                { key: 'infection', label: 'Infection' }
              ].map(criteria => (
                <label key={criteria.key} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData[criteria.key] || false}
                    onChange={(e) => handleInputChange(criteria.key, e.target.checked)}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">{criteria.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 4: // Medical Information
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medical History
              </label>
              <textarea
                value={formData.medical_history || ''}
                onChange={(e) => handleInputChange('medical_history', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
                placeholder="Relevant medical history..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Concomitant Medications
              </label>
              <textarea
                value={formData.concomitant_medications || ''}
                onChange={(e) => handleInputChange('concomitant_medications', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
                placeholder="Other medications taken..."
              />
          </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Allergies
              </label>
              <textarea
                value={formData.allergies || ''}
                onChange={(e) => handleInputChange('allergies', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={2}
                placeholder="Known allergies..."
              />
            </div>
          </div>
        );

      case 5: // Reporter Information
        return (
          <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reported By
            </label>
            <input
              type="text"
              value={formData.reported_by || ''}
              onChange={(e) => handleInputChange('reported_by', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Reporter name"
            />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reporter Contact
              </label>
              <input
                type="text"
                value={formData.reporter_contact || ''}
                onChange={(e) => handleInputChange('reporter_contact', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Phone, email, or other contact info"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Report Source
              </label>
              <select
                value={formData.report_source || 'consumer'}
                onChange={(e) => handleInputChange('report_source', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="consumer">Consumer</option>
                <option value="healthcare_professional">Healthcare Professional</option>
                <option value="manufacturer">Manufacturer</option>
                <option value="distributor">Distributor</option>
              </select>
            </div>
          </div>
        );

      case 6: // Product Information
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lot Number
              </label>
              <input
                type="text"
                value={formData.lot_number || ''}
                onChange={(e) => handleInputChange('lot_number', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Product lot/batch number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Expiration Date
              </label>
              <input
                type="date"
                value={formData.expiration_date || ''}
                onChange={(e) => handleInputChange('expiration_date', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product Usage
              </label>
              <textarea
                value={formData.product_usage || ''}
                onChange={(e) => handleInputChange('product_usage', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
                placeholder="How was the product used? Frequency, duration, etc."
              />
            </div>
          </div>
        );

      case 7: // Review & Submit
        return (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Review Information</h3>
              <div className="space-y-2 text-sm text-gray-900">
                <div><strong className="text-gray-900">Product:</strong> <span className="text-gray-900">{products.find(p => p.id === formData.product_id)?.name || 'Not selected'}</span></div>
                <div><strong className="text-gray-900">Description:</strong> <span className="text-gray-900">{formData.description || 'Not provided'}</span></div>
                <div><strong className="text-gray-900">Severity:</strong> <span className="text-gray-900">{formData.severity || 'mild'}</span></div>
                <div><strong className="text-gray-900">Patient Age:</strong> <span className="text-gray-900">{formData.patient_age || 'Not provided'}</span></div>
                <div><strong className="text-gray-900">Patient Gender:</strong> <span className="text-gray-900">{formData.patient_gender || 'Not provided'}</span></div>
                <div><strong className="text-gray-900">Reported By:</strong> <span className="text-gray-900">{formData.reported_by || 'Not provided'}</span></div>
                <div><strong className="text-gray-900">SAE Criteria:</strong> <span className="text-gray-900">{
                  Object.entries(formData)
                    .filter(([key, value]) => ['death', 'life_threatening', 'hospitalization', 'significant_disability', 'congenital_anomaly', 'required_intervention', 'significant_disfigurement', 'infection'].includes(key) && value)
                    .map(([key]) => key.replace('_', ' '))
                    .join(', ') || 'None'
                }</span></div>
          </div>
        </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => handleInputChange('notes', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={3}
                placeholder="Any additional information..."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create Adverse Event</h2>
            <p className="text-sm text-gray-600">Step {currentStep + 1} of {steps.length}: {steps[currentStep]}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-600 mb-2">
            {steps.map((step, index) => (
              <span key={index} className={index <= currentStep ? 'text-indigo-600 font-medium' : ''}>
                {step}
              </span>
            ))}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Step Content */}
        <div className="mb-6">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          
          {currentStep === steps.length - 1 ? (
          <button
            onClick={handleSubmit}
              disabled={loading || !formData.product_id || !formData.description}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Event'}
          </button>
          ) : (
            <button
              onClick={handleNext}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdverseEventWizard;